import * as Sentry from "@sentry/react";

/**
 * Sentry crash + error reporting.
 *
 * Deliberately DECOUPLED from PostHog's cookie-consent gate: crash reporting is
 * legitimate interest (keeping the product working), so it initializes even when
 * analytics is opted out. It is a no-op when no DSN is configured (local/dev),
 * and every entry point is guarded so observability can never take down the app.
 */

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/** Request/response header names that must never reach Sentry. */
const SENSITIVE_HEADERS = ["authorization", "x-session-token", "cookie", "set-cookie"];

/**
 * Substrings that mark a key as carrying secrets or user/message content.
 * Values under matching keys are redacted from request data + breadcrumbs so
 * chat prompts, message bodies, and tokens never leave the browser.
 */
const SENSITIVE_KEY_HINTS = [
  "token",
  "session",
  "password",
  "secret",
  "api_key",
  "apikey",
  "authorization",
  "message",
  "content",
  "prompt",
  "text",
];

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_HINTS.some((hint) => lower.includes(hint));
}

function scrubHeaders(headers: Record<string, unknown>): void {
  for (const key of Object.keys(headers)) {
    if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
      headers[key] = "[redacted]";
    }
  }
}

function scrubObject(obj: Record<string, unknown>): void {
  for (const key of Object.keys(obj)) {
    if (isSensitiveKey(key)) {
      obj[key] = "[redacted]";
    }
  }
}

/**
 * Scrub PII/secrets before an event is sent: auth headers/cookies, and any
 * request or breadcrumb payload that could embed message/chat content or tokens.
 */
const beforeSend: NonNullable<Sentry.BrowserOptions["beforeSend"]> = (event) => {
  if (event.request) {
    if (event.request.headers) {
      scrubHeaders(event.request.headers as Record<string, unknown>);
    }
    delete event.request.cookies;
    // Request/response bodies can carry chat message + prompt content — never send.
    delete event.request.data;
  }
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.data && typeof crumb.data === "object") {
        scrubObject(crumb.data as Record<string, unknown>);
      }
    }
  }
  return event;
};

let _initialized = false;

/** Initialize Sentry. No-op when the DSN is absent (local/dev) or already done. */
export function initSentry(): void {
  if (_initialized || !DSN) return;
  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.VITE_SENTRY_ENV,
    release: import.meta.env.VITE_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    beforeSend,
  });
  _initialized = true;
}

export interface CaptureContext {
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
}

/**
 * Report an exception to Sentry and return the event id (usable as a
 * user-facing reference). Fails open if the SDK is unavailable.
 */
export function captureException(error: unknown, context?: CaptureContext): string {
  const err =
    error instanceof Error
      ? error
      : new Error(typeof error === "string" ? error : safeStringify(error));
  try {
    return Sentry.captureException(err, {
      tags: context?.tags,
      extra: context?.extra,
      level: context?.level,
    });
  } catch {
    return "";
  }
}

/**
 * Tracks error objects already reported at the API layer so react-query's
 * central onError doesn't double-count the same failure.
 */
const _reported = new WeakSet<object>();

/**
 * Report an API-layer failure exactly once, even if the same error object
 * propagates through both the fetch wrapper and react-query's cache onError.
 */
export function captureApiException(error: unknown, context?: CaptureContext): string {
  if (typeof error === "object" && error !== null) {
    if (_reported.has(error)) return "";
    _reported.add(error);
  }
  return captureException(error, context);
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Generate a correlation id for the `X-Request-ID` header (FE half of FR-7). */
export function newRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
